import { TOnboardingStep } from '@forwardflow/types/onboarding'
import { abandonActiveLoad } from '@forwardflow/store/onboardingStoreV2'

export interface InitializeForOrganizationDeps {
   currentOrganization: string | undefined | null
   /** `UserService.getUserRoleInOrganization`, injected so this is testable. */
   getUserRoleInOrganization: (
      organizationId: string
   ) => Promise<{ roles?: string[] } | undefined>
   initializeOnboardingData: (organizationId: string) => Promise<unknown>
   setIsLoadingOrInitialization: (loading: boolean) => void
   setIsAgent: (isAgent: boolean) => void
   /**
    * `useOnboardingStoreV2.adoptEmptyRecordFor`. The agent path calls this
    * INSTEAD of `setCurrentStep`: moving the step on its own left the
    * previous organization's record underneath it, and wrote the move back
    * into that organization.
    */
   adoptEmptyRecordFor: (
      organizationId: string,
      currentStep: TOnboardingStep
   ) => void
}

/**
 * Abandon whatever selection operation is in flight.
 *
 * Module scope because the operation is: two overlapping selections are two
 * calls to `initializeForOrganization`, and the second has to be able to
 * disown the first. The hook also calls this from its effect cleanup, so
 * unmounting or re-running abandons the request rather than letting it land
 * on whatever is on screen afterwards.
 */
let selectionGeneration = 0
export const invalidateSelection = (): void => {
   selectionGeneration += 1
   /*
    * And the store's load, which is a SEPARATE fence.
    *
    * Bumping only the selection left a gap the agent path walked straight
    * through: A's record GET in flight, B selected, B is an agent so it never
    * enters the store, A's GET returns with a still-matching store generation
    * and commits over B. Unmounting mid-GET was the same hole. Checking
    * before entering initialization is not enough — the load is already
    * inside by then.
    */
   abandonActiveLoad()
}

/**
 * What the loader hook does when the organization becomes known.
 *
 * Extracted from the effect so the SEQUENCING can be tested. Two defects
 * have lived here, and the second was created by fixing the first.
 *
 * ONE: the hook called `initializeOnboardingData` WITHOUT awaiting it and
 * then cleared `isLoadingOrInitialization` immediately, so the store's guard —
 * the one that stops the autosave writing while a load is in flight — was
 * switched off before the load had finished.
 *
 * TWO: the role lookup happens BEFORE the store's own generation fence, and
 * nothing fenced it. Select A, let its lookup stall, select B, let B finish,
 * then let A's lookup return: A entered the store LAST, so it took the newest
 * generation and overwrote B's record while the selected organization was
 * still B. A stale agent response could likewise move B's step and clear B's
 * loading flag.
 *
 * So the whole operation is fenced, not just the part the store protects.
 * `selection` is taken before anything is awaited and re-checked before EVERY
 * store mutation: once inside the `try`, because the agent branch writes
 * three times before returning, and once immediately before initialization,
 * which is where both fall-through paths — a successful non-agent lookup and
 * a failed one — arrive.
 *
 * THREE RULES:
 *
 *   1. Await the load.
 *   2. Do not touch the loading flag on the standard path. Initialization
 *      owns it and clears it itself, in a separate update the autosave
 *      subscriber does not watch. A caller clearing it "as well" is how it
 *      got cleared early.
 *   3. Touch nothing at all once superseded.
 *
 * The agent path is different only in WHICH state it establishes: it adopts
 * an empty record for the selected organization instead of reading one, and
 * that adoption owns the loading flag exactly as a load does.
 */
export const initializeForOrganization = async ({
   currentOrganization,
   getUserRoleInOrganization,
   initializeOnboardingData,
   setIsLoadingOrInitialization,
   setIsAgent,
   adoptEmptyRecordFor,
}: InitializeForOrganizationDeps): Promise<void> => {
   /*
    * Claimed before the first await, so a later selection supersedes this one
    * wherever it happens to be — INCLUDING inside the store, which is why
    * this goes through the same invalidation the hook's cleanup uses rather
    * than bumping the counter directly.
    */
   invalidateSelection()
   const selection = selectionGeneration
   const current = () => selection === selectionGeneration

   if (!currentOrganization) {
      setIsLoadingOrInitialization(false)
      return
   }

   try {
      const userRoleData = await getUserRoleInOrganization(currentOrganization)
      // Superseded while the lookup was out. Neither branch below may run.
      if (!current()) return
      if (userRoleData?.roles?.includes('Agent')) {
         setIsAgent(true)
         /*
          * ESTABLISH this organization's state; do not inherit the previous
          * one's. This path returns without reading anything, so the record
          * in the store is still the organization switched away from — and
          * setting the step on top of it saved that step straight back into
          * that organization, because its write basis was still valid too.
          *
          * This owns the loading flag as initialization does, which is why
          * nothing here clears it.
          */
         adoptEmptyRecordFor(currentOrganization, 'calendar-connection')
         return
      }
   } catch {
      // fall through to standard initialization
   }

   /*
    * The gate for BOTH fall-through paths — a successful non-agent lookup and
    * a failed one. It is the last thing before the store, and the store has a
    * generation of its own that would hand this operation the NEWEST: a
    * superseded one entering here is exactly how the stale organization won.
    *
    * A second check inside the `catch` would be unfalsifiable, since anything
    * it caught this one catches a line later.
    */
   if (!current()) return
   await initializeOnboardingData(currentOrganization)
}
